import type { DetailedCatalystInsight } from '@api/api/catalyst-insights';
import type { Entity } from '@api/api/entity-schema';
import type { ThemeSchemaResponse } from '@api/api/theme-overview';
import { Icon } from '@design/components/icon/Icon';
import { InsightDrawerAssetHeaderContent } from '@terminal/components/insight-drawer-header/InsightDrawerAssetHeaderContent';
import { InsightDrawerHeaderContent } from '@terminal/components/insight-drawer-header/InsightDrawerHeaderContent';
import { InsightTypePill } from '@terminal/components/insight-type-pill/InsightTypePill';
import { InsightsLivePrice } from '@terminal/components/insights-live-price/InsightsLivePrice';
import {
  POSTHOG_TRACKING_SOURCES,
  type PosthogTrackingSource,
} from '@terminal/services/tracking/posthog/constants';
import { CardType } from '@terminal/views/insight-feed/components/insight-feed-card/InsightFeedCard';
import { ThemeLogoIcon } from '@terminal/views/theme-overview/components/theme-logo-icon/ThemeLogoIcon';
import { EntityHeaderWatchlist } from '@terminal/widgets/earnings-insight-drawer/earnings-insight-widget/components/entity-header-watchlist/EntityHeaderWatchlist';
import type { ComponentProps } from 'react';

import styles from './CatalystDrawerHeader.module.scss';

interface MacroGeoCategoryConfig {
  fillColor: string;
  iconName: ComponentProps<typeof Icon>['iconName'];
}

interface CatalystSubjectHeaderProps {
  catalystInsight: DetailedCatalystInsight;
  entity?: Entity;
  isCompanyCatalyst?: boolean;
  shouldShowRedesignedCompanyHeader: boolean;
  theme?: ThemeSchemaResponse;
  translatedSubCategory: string;
}

interface CatalystMetadataHeaderProps {
  catalystInsight: DetailedCatalystInsight;
  isCompanyCatalyst?: boolean;
  shouldShowRedesignedCompanyHeader: boolean;
  themeId?: string;
  translatedSubCategory: string;
}

interface ChartEntityHeaderProps {
  entity?: Entity;
  isMultipleEntitiesChart: boolean;
  variant?: 'default' | 'compact';
  trackingSource?: PosthogTrackingSource;
}

const getMacroGeoCategoryConfig = (
  subCategory: DetailedCatalystInsight['sub_category']
): MacroGeoCategoryConfig | null => {
  if (subCategory === 'geopolitical_global_events') {
    return {
      fillColor: 'var(--icon-decorative-purple)',
      iconName: 'Geopolitical',
    };
  }

  if (subCategory === 'macroeconomic_policy_changes') {
    return {
      fillColor: 'var(--text-decorative-teal)',
      iconName: 'Macroeconomics',
    };
  }

  return null;
};

const renderCompanySubjectHeader = ({
  entity,
  useRedesignedHeader,
}: {
  entity: Entity;
  useRedesignedHeader: boolean;
}) => {
  if (useRedesignedHeader) {
    return (
      <InsightDrawerAssetHeaderContent
        entity={entity}
        source={POSTHOG_TRACKING_SOURCES.CATALYST_INSIGHT_DRAWER}
      />
    );
  }

  return (
    <div className={styles.entityAndPriceWrapper}>
      <EntityHeaderWatchlist entity={entity} logoSize={48} />
      <InsightsLivePrice entity={entity} showPricePercentage waitForLiveTick />
    </div>
  );
};

export const renderCatalystSubjectHeader = ({
  catalystInsight,
  entity,
  isCompanyCatalyst,
  shouldShowRedesignedCompanyHeader,
  theme,
  translatedSubCategory,
}: CatalystSubjectHeaderProps) => {
  if (isCompanyCatalyst && entity) {
    return renderCompanySubjectHeader({
      entity,
      useRedesignedHeader: shouldShowRedesignedCompanyHeader,
    });
  }

  if (theme) {
    return (
      <div className={styles.iconWrapper}>
        <ThemeLogoIcon
          logo_url={theme.logo_url}
          name={theme.name}
          type={theme.type}
        />
        <p className={styles.categoryTitle}>{theme.name}</p>
      </div>
    );
  }

  const macroGeoCategory = getMacroGeoCategoryConfig(
    catalystInsight.sub_category
  );

  if (!macroGeoCategory) {
    return null;
  }

  return (
    <div className={styles.iconWrapper}>
      <div className={styles.marketWideIconWrapper}>
        <Icon
          fillColor={macroGeoCategory.fillColor}
          iconName={macroGeoCategory.iconName}
          size={48}
        />
      </div>
      <p className={styles.categoryTitle}>{translatedSubCategory}</p>
    </div>
  );
};

export const renderCatalystMetadataHeader = ({
  catalystInsight,
  isCompanyCatalyst,
  shouldShowRedesignedCompanyHeader,
  themeId,
  translatedSubCategory,
}: CatalystMetadataHeaderProps) => {
  if (shouldShowRedesignedCompanyHeader) {
    return (
      <InsightDrawerHeaderContent
        cardType={
          isCompanyCatalyst ? CardType.CompanyCatalyst : CardType.MarketCatalyst
        }
        context={`${translatedSubCategory} · ${catalystInsight.action_type}`}
        publishedAt={catalystInsight.created_at}
        title={catalystInsight.title}
      />
    );
  }

  return (
    <>
      <InsightTypePill
        createdAt={catalystInsight.created_at}
        subCategory={catalystInsight.sub_category}
        category={catalystInsight.category}
      />
      <div>
        <div className={styles.subCategoryActionTypeWrapper}>
          {themeId ||
          catalystInsight.sub_category === 'geopolitical_global_events' ? (
            <div className={styles.titleWrapper}>
              <span>{`${catalystInsight.action_type}`}</span>
            </div>
          ) : (
            `${translatedSubCategory} · ${catalystInsight.action_type}`
          )}
        </div>
        <div className={styles.title}>{catalystInsight.title}</div>
      </div>
    </>
  );
};

export const renderLegacyChartEntityHeader = ({
  entity,
  isMultipleEntitiesChart,
  variant = 'default',
  trackingSource,
}: ChartEntityHeaderProps) => {
  if (isMultipleEntitiesChart || !entity) {
    return null;
  }

  return (
    <div className={styles.watchlistRow}>
      <EntityHeaderWatchlist
        entity={entity}
        trackingSource={trackingSource}
        variant={variant}
      />
    </div>
  );
};
